import config from '@/configs/app'
import ads, { setAdsToken } from '@/services/googles/ads'

const LOGIN_CUSTOMER_ID = config.ads.loginCustomerId
const ALL_ID = -1

function initState () {
  return {
    currentCustomer: null,

    customers: [],
    campaigns: {},
    budgets: {},
    adGroups: {},
    adGroupAds: {},
    adGroupKeywords: {}
  }
}

function updateToken (getters) {
  const token = getters['user/token']
  setAdsToken(token, LOGIN_CUSTOMER_ID)
}

function cacheHandler (docs, reload) {
  const items = docs || []
  return (!reload)
    ? (items.length > 0) && items
    : null
}

const state = initState()

const getters = {
  currentCustomer: (state) => (state.currentCustomer),
  currentCustomerId: (state) => ((state.currentCustomer || {}).id),

  customers: (state) => (state.customers || []),
  campaigns: (state) => (id) => (state.campaigns[id] || []),
  budgets: (state) => (id) => (state.budgets[id] || []),
  adGroups: (state) => (id) => (state.adGroups[id] || []),
  adGroupAds: (state) => (id) => (state.adGroupAds[id] || []),
  adGroupKeywords: (state) => (id) => (state.adGroupKeywords[id] || []),

  budgetDefault: (state) => (id) => {
    const budgets = state.budgets[id] || []
    const items = budgets.filter(b => (b.campaignBudget.name === 'budget-toolkit'))
    return (items[0])
  },
  budgetDefaultId: (state, getters) => (id) => {
    const item = getters.budgetDefault(id)
    return (item) ? item.campaignBudget.id : null
  }
}

const mutations = {
  reset: (state) => {
    const vals = initState()
    Object.keys(vals).map(k => (state[k] = vals[k]))
  },
  currentCustomer: (state, val) => (state.currentCustomer = val),

  customers: (state, val) => (state.customers = val),
  campaigns: (state, { id, val }) => (state.campaigns[id] = val),
  budgets: (state, { id, val }) => (state.budgets[id] = val),
  adGroups: (state, { id, val }) => (state.adGroups[id] = val),
  adGroupAds: (state, { id, val }) => (state.adGroupAds[id] = val),
  adGroupKeywords: (state, { id, val }) => (state.adGroupKeywords[id] = val)
}

const actions = {
  reset: ({ commit }) => (commit('reset')),
  setCurrentCustomer: ({ commit }, value) => (commit('currentCustomer', value)),

  loadCustomers: async ({ commit, rootGetters }) => {
    updateToken(rootGetters)

    const items = await ads.getCustomers()
    commit('customers', items)
    return items
  },

  loadCampaigns: async ({ commit, getters, rootGetters }, { customerId, reload }) => {
    updateToken(rootGetters)

    const id = customerId || ALL_ID
    const cache = cacheHandler(getters.campaigns(id), reload)
    if (cache) { return cache }

    const items = await ads.getCampaigns(customerId)
    commit('campaigns', { id: id, val: items })

    return items
  },

  loadBudgets: async ({ commit, getters, rootGetters }, { customerId, reload }) => {
    updateToken(rootGetters)

    const id = customerId || ALL_ID
    const cache = cacheHandler(getters.budgets(id), reload)
    if (cache) { return cache }

    const items = await ads.getBudgets(customerId)
    commit('budgets', { id: id, val: items })

    return items
  },

  loadAdGroups: async ({ commit, getters, rootGetters }, { customerId, campaignId, reload }) => {
    updateToken(rootGetters)

    const id = campaignId || ALL_ID
    const cache = cacheHandler(getters.adGroups(id), reload)
    if (cache) { return cache }

    const items = await ads.getAdGroups(customerId, campaignId)
    commit('adGroups', { id: id, val: items })

    return items
  },

  loadAdGroupAds: async ({ commit, getters, rootGetters }, { customerId, adGroupId, reload }) => {
    updateToken(rootGetters)

    const id = adGroupId || ALL_ID
    const cache = cacheHandler(getters.adGroupAds(id), reload)
    if (cache) { return cache }

    const items = await ads.getAdGroupAds(customerId, adGroupId)
    commit('adGroupAds', { id: id, val: items })

    return items
  },

  loadAdGroupKeywords: async ({ commit, getters, rootGetters }, { customerId, adGroupId, reload }) => {
    updateToken(rootGetters)

    const id = adGroupId || ALL_ID
    const cache = cacheHandler(getters.adGroupKeywords(id), reload)
    if (cache) { return cache }

    const items = await ads.getAdGroupKeywords(customerId, adGroupId)
    commit('adGroupKeywords', { id: id, val: items })

    return items
  },

  createBudgetDefault: async ({ rootGetters }, { customerId }) => {
    updateToken(rootGetters)

    // minimun amount is here: https://developers.google.com/adwords/api/docs/appendix/codes-formats#currency-codes
    const create = {
      name: 'budget-toolkit',
      amount_micros: 10000
    }

    const res = await ads.addBudget(customerId, create)
    return res
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
